import { humaniseCatalogItemText } from "@automatry/schemas";

export const cx = (...values: (string | false | undefined)[]): string =>
  values.filter(Boolean).join(" ");

const titleCase = (value: string): string =>
  value.replace(/\b\w/g, (match) => match.toUpperCase());

const titleCaseWords = (value: string): string =>
  value
    .replace(/[_:]+/g, "-")
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizeSpaces = (value: string): string => value.replace(/\s+/g, " ").trim();

const monthLabel = (month: string): string => {
  const monthIndex = Number(month) - 1;
  const labels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ];

  return labels[monthIndex] ?? month;
};

export const plainCatalogRelease = (releaseId: string): string => {
  const cleaned = releaseId.replace(/^catalog-release:/, "");
  const match = cleaned.match(/^([a-z]+)-(\d{4})-(\d{2})-(.+)$/i);

  if (match) {
    const brand = match[1] ?? "";
    const year = match[2] ?? "";
    const month = match[3] ?? "";
    const releaseName = match[4] ?? "";
    return `${titleCaseWords(brand)} ${titleCaseWords(releaseName)} price list (${monthLabel(month)} ${year})`;
  }

  return `${titleCaseWords(cleaned)} price list`;
};

export const plainRuleSet = (ruleSetId: string): string => {
  const cleaned = ruleSetId.replace(/^baseline-/, "");
  const match = cleaned.match(/^(\d{4})-(\d{2})$/);

  if (match) {
    const year = match[1] ?? "";
    const month = match[2] ?? "";
    return `Baseline controls rules (${monthLabel(month)} ${year})`;
  }

  return `${titleCaseWords(cleaned)} rules`;
};

const projectKeyFromId = (projectId: string): string =>
  projectId
    .trim()
    .toLowerCase()
    .replace(/^created-/, "");

const projectWordsFromId = (projectId: string): string =>
  normalizeSpaces(projectKeyFromId(projectId).replaceAll("-", " "));

const stripCreatedProjectPrefix = (id: string, projectId?: string): string => {
  const normalized = id.trim().toLowerCase();

  if (!normalized.startsWith("created-")) {
    return normalized;
  }

  const withoutCreated = normalized.replace(/^created-/, "");
  const projectKey = projectId ? projectKeyFromId(projectId) : "";
  const exactPrefix = projectKey ? `${projectKey}-` : "";
  const withoutProject =
    exactPrefix && withoutCreated.startsWith(exactPrefix)
      ? withoutCreated.slice(exactPrefix.length)
      : withoutCreated;
  const withoutSuffix = withoutProject.replace(/-\d+$/, "");

  if (!exactPrefix) {
    const parts = withoutSuffix.split("-");
    const typeIndex = parts.findIndex((part, index) => {
      const next = parts[index + 1];

      return part === "type" && next !== undefined && /^[a-z0-9]+$/.test(next);
    });

    if (typeIndex > 0 && typeIndex <= 2) {
      return parts.slice(typeIndex).join("-");
    }

    if (parts.length > 2) {
      return parts.slice(2).join("-");
    }
  }

  return withoutSuffix;
};

const cleanApartmentLabel = (value: string): string =>
  titleCase(value.replaceAll("-", " "))
    .replace(/\b(One|Two|Three|Four|Five|Six|Seven|Eight|Nine|\d+) Bed\b/g, "$1-Bed")
    .replace(/\b(Ux|Ui|Io|Fcu|Ufh|Eufh|Hiu|Ciu|Bms|Psu)\b/g, (match) =>
      match.toUpperCase()
    );

export const labelFromId = (id: string, projectId?: string): string =>
  cleanApartmentLabel(
    stripCreatedProjectPrefix(id, projectId).replace(/^carrington-/, "")
  );

export interface ApartmentTypeLabelInput {
  apartmentTypeId: string;
  rawLabel?: string | null;
  name?: string | null;
  beds?: number | null;
  projectId?: string | null;
  projectName?: string | null;
}

const NUMBER_WORDS: Record<number, string> = {
  1: "one",
  2: "two",
  3: "three",
  4: "four",
  5: "five",
  6: "six",
  7: "seven",
  8: "eight",
  9: "nine"
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const stripTextPrefix = (
  value: string,
  prefix: string | null | undefined
): {
  value: string;
  stripped: boolean;
} => {
  const cleanPrefix = normalizeSpaces(prefix ?? "");

  if (!cleanPrefix) {
    return { value, stripped: false };
  }

  const pattern = new RegExp(`^${escapeRegExp(cleanPrefix)}(?:\\s+|$)`, "i");

  if (!pattern.test(value)) {
    return { value, stripped: false };
  }

  return { value: normalizeSpaces(value.replace(pattern, "")), stripped: true };
};

const stripGeneratedLabelPrefix = (
  value: string,
  input: ApartmentTypeLabelInput
): string => {
  let label = normalizeSpaces(value);
  const startedCreated = /^created\s+/i.test(label);

  label = normalizeSpaces(label.replace(/^created\s+/i, ""));

  const projectNameResult = stripTextPrefix(label, input.projectName);

  label = projectNameResult.value;

  const projectIdResult = stripTextPrefix(
    label,
    input.projectId ? projectWordsFromId(input.projectId) : undefined
  );

  label = projectIdResult.value;

  if (startedCreated || projectNameResult.stripped || projectIdResult.stripped) {
    label = normalizeSpaces(label.replace(/\s+\d+$/u, ""));
  }

  return label;
};

export const bedroomLabel = (beds: number | null | undefined): string | undefined => {
  if (typeof beds !== "number" || !Number.isFinite(beds) || beds < 0) {
    return undefined;
  }

  const count = Math.floor(beds);

  if (count === 0) {
    return "Studio";
  }

  return `${count}-bed`;
};

const bedroomTailVariants = (beds: number): string[] => {
  if (beds === 0) {
    return ["studio"];
  }

  const word = NUMBER_WORDS[beds];
  const numbers = [String(beds), ...(word ? [word] : [])];

  return numbers.flatMap((value) => [
    `${value} bed`,
    `${value}-bed`,
    `${value} bedroom`,
    `${value}-bedroom`,
    `${value} bedrooms`,
    `${value}-bedrooms`
  ]);
};

const stripBedroomTail = (value: string, beds: number): string => {
  let label = value;

  for (const variant of bedroomTailVariants(beds)) {
    const pattern = new RegExp(
      `(?:\\s*[·|,/\\-–—:]\\s*|\\s+)${escapeRegExp(variant)}\\.?$`,
      "i"
    );
    const next = normalizeSpaces(label.replace(pattern, ""));

    if (next !== label && next.length > 0) {
      label = next;
    }
  }

  return label;
};

const isOnlyBedroomDescriptor = (value: string, beds: number): boolean => {
  const normalized = value.toLowerCase().replace(/[–—]/g, "-");

  return bedroomTailVariants(beds).some(
    (variant) => normalized === variant.toLowerCase()
  );
};

export const apartmentTypeDisplayLabel = (input: ApartmentTypeLabelInput): string => {
  const sourceLabel = normalizeSpaces(input.rawLabel ?? input.name ?? "");
  const fallback = labelFromId(input.apartmentTypeId, input.projectId ?? undefined);
  const base = stripGeneratedLabelPrefix(sourceLabel || fallback, input) || fallback;
  const beds =
    typeof input.beds === "number" && Number.isFinite(input.beds)
      ? Math.floor(input.beds)
      : undefined;
  const bedsLabel = bedroomLabel(beds);

  if (beds === undefined || bedsLabel === undefined) {
    return base;
  }

  if (isOnlyBedroomDescriptor(base, beds)) {
    return bedsLabel;
  }

  const withoutBedroomTail = stripBedroomTail(base, beds);

  if (withoutBedroomTail.length === 0 || withoutBedroomTail === base) {
    return withoutBedroomTail.length === 0 ? bedsLabel : `${base} · ${bedsLabel}`;
  }

  return `${withoutBedroomTail} · ${bedsLabel}`;
};

export const solutionDisplayName = (
  apartmentTypeId: string,
  opts?: { projectId?: string | null; rawLabel?: string | null; beds?: number | null }
): string =>
  apartmentTypeDisplayLabel({
    apartmentTypeId,
    rawLabel: opts?.rawLabel ?? null,
    beds: opts?.beds ?? null,
    projectId: opts?.projectId ?? null
  });

export const ruleTraceDisplayLabel = (ruleTraceRef: string): string =>
  titleCaseWords(ruleTraceRef.replace(/^trace[-_]/i, ""))
    .replace(/\bFcu\b/g, "FCU")
    .replace(/\bUfh\b/g, "UFH")
    .replace(/\bEufh\b/g, "EUFH")
    .replace(/\bBms\b/g, "BMS")
    .replace(/\bIo\b/g, "I/O")
    .replace(/\bCom (\d+)r\b/g, "COM-$1R")
    .replace(/\bCom\b/g, "COM")
    .replace(/\bTs(\d)\b/g, "TS-$1");

export const projectLabelFromId = (projectId: string): string => {
  if (projectId === "carrington") {
    return "Carrington";
  }

  return labelFromId(projectId.replace(/^created-/, ""));
};

export const projectFilenameStem = (projectId: string): string =>
  projectId
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "project";

export { humaniseCatalogItemText };

const IDENTIFIER_TEXT_REPLACEMENTS: readonly [RegExp, string][] = [
  [/\bquestionsmanual_entry\b/gi, "manual question entry"],
  [/\bcontrol_temperature_sensor\b/gi, "control temperature sensor"],
  [/\bcontrol_temperature\b/gi, "control temperature"],
  [/\bufh_limit_active\b/gi, "active underfloor heating limit sensor"],
  [/\bufh_limit_spare\b/gi, "spare underfloor heating limit sensor"],
  [/\bcatalog_workbook\b/gi, "catalogue workbook"],
  [/\brich_rules_workbook\b/gi, "Rich rules workbook"],
  [/\bmanual_entry\b/gi, "manual entry"],
  [/\bclient_spec\b/gi, "client spec"],
  [/\bclient_email\b/gi, "client email"],
  [/\bbaseline_note\b/gi, "baseline note"],
  [/\bproposal_forge_generated\b/gi, "generated proposal"]
];

const generatedDeviceMapPrefixPattern = /\b(?:device|physical)-map-[A-Za-z0-9_-]+:\s*/g;

const ruleTraceTokenPattern = /\btrace[-_][A-Za-z0-9_-]+\b/g;

const replaceIdentifierTokens = (value: string): string =>
  IDENTIFIER_TEXT_REPLACEMENTS.reduce(
    (current, [pattern, replacement]) => current.replace(pattern, replacement),
    value
  );

export const humaniseIdentifierText = (value: string | null | undefined): string => {
  const cleaned = humaniseCatalogItemText(replaceIdentifierTokens(value ?? ""));

  if (!cleaned) {
    return "";
  }

  const withoutGeneratedMapPrefixes = cleaned.replace(
    generatedDeviceMapPrefixPattern,
    ""
  );
  const withRuleTraceLabels = withoutGeneratedMapPrefixes.replace(
    ruleTraceTokenPattern,
    (match) => ruleTraceDisplayLabel(match)
  );
  const replaced = replaceIdentifierTokens(withRuleTraceLabels);

  return replaced.replace(/\s+/g, " ").trim();
};
